<template>
  <div>
    <b-modal
      ref="modal-refund"
      id="modal-refund"
      class="modal-child"
      size="w-add-fund"
      hide-footer
      hide-header
      hide-header-close
      centered
      no-close-on-backdrop
    >
      <!--Header-->
      <div class="d-flex justify-content-between top-modal">
        <h3
data-testid="modal-refund-title"
class="font-medium text-primary"
>
          {{ $t("myAdAccountsPage.refundModal.title") }}
        </h3>
        <div>
          <feather-icon
            data-testid="close-icon"
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hideModal"
          />
        </div>
      </div>

      <!-- Account Info -->
      <b-row class="pb-2 mx-0 content-top">
        <b-col
md="12"
cols="12"
class="px-0"
>
          <b-row>
            <b-col
cols="12"
md="4"
class="pr-0"
>
              {{ $t("payment.textAdAccountName") }}
            </b-col>
            <b-col
              v-b-tooltip.hover.top.v-light="accountName"
              cols="12"
              md="8"
              class="text-secondary font-medium wrap-content"
            >
              {{ accountName }}
            </b-col>
          </b-row>
          <b-row class="mx-div">
            <b-col
cols="12"
md="4"
>
              {{ $t("payment.textAdAccountID") }}
            </b-col>
            <b-col
cols="12"
md="8"
class="text-secondary font-medium"
>
              {{ accountId }}
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <!-- Refund Info -->
      <div class="refund-info">
        <p class="text-secondary mb-2">
          {{ $t("myAdAccountsPage.refundModal.description") }}
        </p>
        <ul class="refund-list text-secondary">
          <li v-html="$t('myAdAccountsPage.refundModal.disableCampaigns')" />
          <li v-html="$t('myAdAccountsPage.refundModal.removeAdAccount')" />
          <li
            v-html="
              $t('myAdAccountsPage.refundModal.transferBalance', {
                amount: refundAmount,
                platform: `${convertPlatformName} Balance`
              })
            "
          />
        </ul>
      </div>

      <!-- Warning -->
      <div class="warning-box d-flex align-items-center mt-2">
        <feather-icon
          icon="AlertTriangleIcon"
          size="18"
          class="text-warning mr-1 flex-shrink-0"
        />
        <span class="text-warning-content">
          {{ $t("myAdAccountsPage.refundModal.warningCannotUndo") }}
        </span>
      </div>

      <!-- Footer -->
      <div class="d-flex justify-content-end mt-2">
        <btn-loading
          id="cancel-refund"
          variant-convert="flat-secondary"
          pill
          @click="hideModal"
        >
          {{ $t("common.btnCancel") }}
        </btn-loading>
        <btn-loading
          id="submit-refund"
          class="ml-1"
          variant-convert="btn-submit"
          pill
          :loading="loadingSubmit"
          @click="submitRefund"
        >
          {{ $t("myAdAccountsPage.refundModal.btnConfirmRefund") }}
        </btn-loading>
      </div>
    </b-modal>

    <!-- Success Modal -->
    <b-modal
      ref="modal-refund-success"
      id="modal-refund-success"
      size="sm"
      hide-footer
      hide-header
      hide-header-close
      centered
      no-close-on-backdrop
    >
      <div class="text-center py-2">
        <b-img
          :src="
            require('@/assets/images/pages/payment/ic-payment-success-2.svg')
          "
          alt="success"
          class="mb-2"
          width="64"
          height="64"
        />
        <h4 class="font-medium mb-1">
          {{ $t("myAdAccountsPage.refundModal.successTitle") }}
        </h4>
        <p class="text-secondary">
          {{ $t("myAdAccountsPage.refundModal.successDescription") }}
        </p>
      </div>
      <div class="d-flex justify-content-center pb-1">
        <btn-loading
          id="done-refund"
          variant-convert="btn-submit"
          pill
          class="w-100"
          @click="closeSuccessModal"
        >
          {{ $t("myAdAccountsPage.refundModal.btnDone") }}
        </btn-loading>
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import {
  BModal, BRow, BCol, BImg, VBTooltip,
} from 'bootstrap-vue'
import BtnLoading from '@/layouts/components/BtnLoading.vue'
import numberFormatMixin from '@/mixins/numberFormatMixin'
import envMixin from '@/mixins/envMixin'
import billingMixin from '@/mixins/billingMixin'
import { createNamespacedHelpers } from 'vuex'
import { toastification } from '@core/mixins/toast'

const { mapActions, mapGetters } = createNamespacedHelpers('payment')
const { mapActions: mapActionsAuth } = createNamespacedHelpers('auth')

export default {
  components: {
    BModal,
    BRow,
    BCol,
    BImg,
    BtnLoading,
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  mixins: [numberFormatMixin, billingMixin, toastification, envMixin],

  props: {
    account: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      loadingSubmit: false,
    }
  },

  computed: {
    ...mapGetters(['status', 'message']),

    accountName() {
      return this.account?.name || ''
    },

    accountId() {
      return this.account?._id || ''
    },

    refundAmount() {
      const { currency, balance } = this.account
      if (!balance) return '0.00'
      const rate = this.USDToVNDRate
      const amount = currency === 'VND' ? this.amountFormat(balance / rate) : balance
      return Number(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
  },

  methods: {
    ...mapActions(['requestRefundSuspendedBalance']),
    ...mapActionsAuth(['getAccountInfo']),

    showModal() {
      this.$refs['modal-refund'].show()
    },

    hideModal() {
      this.$refs['modal-refund'].hide()
    },

    async submitRefund() {
      this.loadingSubmit = true
      try {
        const params = {
          adsAccountId: this.accountId,
        }

        await this.requestRefundSuspendedBalance(params)

        if (this.status) {
          this.hideModal()
          this.$refs['modal-refund-success'].show()
          this.$emit('fetch-ads-accounts')
          this.getAccountInfo()
        } else {
          this.toastFailure(this.message || 'Service temporarily unavailable')
        }
      } catch (error) {
        this.toastFailure(error?.message || 'Refund failed')
      } finally {
        this.loadingSubmit = false
      }
    },

    closeSuccessModal() {
      this.$refs['modal-refund-success'].hide()
    },
  },
}
</script>

<style lang="scss">
#modal-refund {
  .modal-content {
    border-radius: 32px !important;
  }

  .modal-body {
    @media (min-width: 767px) {
      padding: 2rem !important;
    }
  }
}

#modal-refund-success {
  .modal-content {
    border-radius: 32px !important;
  }

  .modal-body {
    @media (min-width: 767px) {
      padding: 2rem !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.top-modal {
  margin-bottom: 30px;
}

.content-top {
  border-bottom: 1px solid rgba(22, 33, 62, 0.1);
  margin-bottom: 24px;
}

.mx-div {
  margin-top: 12px;
  margin-bottom: 12px;
}

.text-secondary {
  color: #3d405b !important;
}

.wrap-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.refund-info {
  .refund-list {
    padding-left: 20px;
    margin-bottom: 0;

    li {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.warning-box {
  background-color: #fffbeb;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 10px 16px;

  .text-warning-content {
    color: #92400e;
    font-size: 14px;
  }
}
</style>
